/**
 * What differs between the browser and a desktop (NW.js) build of the wallet, in one module:
 * where the configuration comes from, and how a wallet file leaves and enters the page.
 *
 * Browser version: the configuration is GET /config.json from the wallet's own server, a wallet file is
 * saved through a download and opened through a file input. A desktop build swaps this module.
 */

import type { WalletData } from 'streembit-bc-sdk';
import type { ChainStatus } from 'streembit-bc-sdk/api';

export interface WalletConfig {
    /** base URL of the node API: submission, contract metadata and the catalogue */
    apiBaseUrl: string;
    /** base URL of the read API: status, balances and history; the node API when the deployment has no read mirror */
    readApiBaseUrl: string;
    /** the status as the server last read it from the read API, for the first paint; null when the server has none */
    status: ChainStatus | null;
}

/** Network name written into a wallet file opened without one. */
const FALLBACK_NETWORK = 'testnet';

export async function loadConfig(): Promise<WalletConfig> {
    const response = await fetch('/config.json', { cache: 'no-store' });
    if (!response.ok) throw new Error(`/config.json answered ${response.status}`);
    const body = await response.json() as { apiBaseUrl?: unknown; readApiBaseUrl?: unknown; status?: unknown };
    if (typeof body.apiBaseUrl !== 'string' || !body.apiBaseUrl) throw new Error('/config.json has no apiBaseUrl');
    if (typeof body.readApiBaseUrl !== 'string' || !body.readApiBaseUrl) throw new Error('/config.json has no readApiBaseUrl');
    const s = body.status as Partial<ChainStatus> | null | undefined;
    const status = s && typeof s.chainId === 'number' && typeof s.version === 'number' && typeof s.network === 'string' && typeof s.height === 'number'
        ? { chainId: s.chainId, version: s.version, network: s.network, height: s.height }
        : null;
    return { apiBaseUrl: body.apiBaseUrl, readApiBaseUrl: body.readApiBaseUrl, status };
}

/** Offers the sealed wallet file as a download named after the address. */
export function downloadWalletFile(file: WalletData, address: string): void {
    const blob = new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `streembit-wallet-${address.slice(0, 8)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Reads a wallet file the user chose and checks its shape before the password is tried against it. */
export async function readWalletFile(chosen: File): Promise<WalletData> {
    return parseWalletFile(await chosen.text());
}

function parseWalletFile(text: string): WalletData {
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        throw new Error('The file is not JSON');
    }
    if (!parsed || typeof parsed !== 'object') throw new Error('The file is not a wallet file');
    const file = parsed as Partial<WalletData>;
    if (typeof file.encrypted !== 'string' || typeof file.salt !== 'string' || typeof file.iv !== 'string' || typeof file.authTag !== 'string') {
        throw new Error('The file is not a Streembit wallet file');
    }
    if (file.kdf !== 'scrypt' || !file.kdfParams || typeof file.kdfParams !== 'object') {
        throw new Error('The wallet file does not use scrypt key derivation and cannot be opened');
    }
    return {
        version: typeof file.version === 'number' ? file.version : 0,
        encrypted: file.encrypted,
        salt: file.salt,
        iv: file.iv,
        authTag: file.authTag,
        kdf: file.kdf,
        kdfParams: file.kdfParams,
        network: typeof file.network === 'string' ? file.network : FALLBACK_NETWORK,
        createdAt: typeof file.createdAt === 'string' ? file.createdAt : new Date().toISOString(),
        metadata: file.metadata && typeof file.metadata === 'object' ? file.metadata : {}
    };
}
